// Type imports
import type React from 'react'
import type { NextPage, GetStaticProps } from 'next'
import type { ActionTypes } from '../types'
import type { RequiredNs as ComponentNs } from '../components/localized/types'

// External imports
import { useRouter } from 'next/router'
import { NextSeo } from 'next-seo'
import { Trans, useTranslation } from 'react-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'

// Misc
import useScrollPassed from '../hooks/use-scrolled-passed'

import Header from '../components/localized/Header'
import Footer from '../components/localized/Footer'
import Testimonials from '../components/localized/Testimonials'

import { HeroSection, Title as HeroTitle, Description as HeroDescription } from '../components/Hero'
import { Section } from '../components/Section'
import Highlight from '../components/Highlight'
import BottomCta from '../components/BottomCta'
import VsTable from '../components/VsTable'

import PrimaryAction from '../components/PrimaryAction'

// Constants imports
import { defaultPageSpecificSEO, pageSpecificCtaLink } from '../constants'

/**
 * Load all the namespaces required for this page include all localized components
 */
type RequiredNs = 'vs-grammarly' | 'common' | ComponentNs
const namespaces: Record<RequiredNs, true> = { common: true, 'vs-grammarly': true }
export const getStaticProps: GetStaticProps = async ({ locale }) => ({
  props: {
    ...(await serverSideTranslations(locale ?? '', Object.keys(namespaces)))
  }
})

const VsGrammarly: NextPage = () => {
  const router = useRouter()
  const { t } = useTranslation('vs-grammarly')
  const { t: tCommon } = useTranslation('common')

  const ctaAction: ActionTypes = { ...t('CTA_BUTTON'), ...pageSpecificCtaLink(router.pathname, router.locale) }

  // Controls for the header to only go sticky after user passes the fold
  const { ref: heroCtaRef, scrolledPassed } = useScrollPassed()

  const commonVsProps = {
    colOne: {
      brand: tCommon('OUTWRITE'),
      checkIconAlt: tCommon('iconAlt.OUTWRITE_HAS_FEATURE'),
      closeIconAlt: tCommon('iconAlt.OUTWRITE_DOESNT_HAVE_FEATURE')
    },
    colTwo: {
      brand: t('GRAMMARLY'),
      checkIconAlt: t('GRAMMARLY_HAS_FEATURE_ICON_ALT'),
      closeIconAlt: t('GRAMMARLY_DOESNT_HAVE_FEATURE_ICON_ALT')
    }
  }

  return (
    <>
      <NextSeo
        {...defaultPageSpecificSEO(router.pathname, router.locale)}
        {...t('SEO')}
      />

      {/* Hero */}
      <Header t={tCommon} action={ctaAction} data-sticky={scrolledPassed} />
      <HeroSection>
        <HeroTitle>
          <Trans t={t} i18nKey='hero.TITLE' components={[<Highlight color='yellow' key={0} />]} />
        </HeroTitle>
        <HeroDescription>{t('hero.DESCRIPTION')}</HeroDescription>
        <PrimaryAction {...ctaAction} ref={heroCtaRef} clickId='hero-cta' />
      </HeroSection>

      <Section>
        <VsTable
          {...commonVsProps}
          title={t('pricing.TITLE')}
          features={[
            { name: t('pricing.CHEAPER_PLANS'), colOne: true, colTwo: false },
            { name: t('pricing.MONTHLY_YEARLY_PLANS'), colOne: true, colTwo: true }
          ]}
        />
        <VsTable
        {...commonVsProps}
          title={t('features.TITLE')}
          features={[
            { name: t('features.REWRITE'), colOne: true, colTwo: false },
            { name: t('features.MULTILANGUAGE_SUPPORT'), colOne: true, colTwo: false },
            { name: t('features.ENABLE_DISABLE_SUGGESTIONS'), colOne: true, colTwo: false },
            { name: t('features.GRAMMAR_CHECKING'), colOne: true, colTwo: true },
            { name: t('features.THESAURUS'), colOne: true, colTwo: true }
          ]}
        />
        <VsTable
        {...commonVsProps}
          title={t('platforms.TITLE')}
          features={[
            { name: t('platforms.GOOGLE_DOCS'), colOne: true, colTwo: true },
            { name: t('platforms.MS_WORD'), colOne: true, colTwo: true },
            { name: t('platforms.BROWSER_EXTENSION'), colOne: true, colTwo: true },
            { name: t('platforms.WEB_APP'), colOne: true, colTwo: true }
          ]}
        />
      </Section>

      {/* Testimonials */}
      <Testimonials
        t={tCommon}
        description={t('testimonials.DESCRIPTION')}
      />

      {/* BottomCta */}
      <BottomCta
        action={ctaAction}
        {...t('BOTTOM_CTA')}
      />

      <Footer t={tCommon} />
    </>
  )
}

export default VsGrammarly
